import groupBy from "lodash.groupby";
import sortBy from "lodash.sortby";
import React, { useState } from "react";
import { SanitySubject, SanityTopic } from "../../model/common";
import { FilterCounts, FilterType } from "../Listing/filters";

import classNames from "classnames";
import PlainButton from "../Button/PlainButton/PlainButton";
import Checkbox from "../Inputs/Checkbox/Checkbox";
import Text from "../Text/Text";
import GetText from "../../i18n/GetText";
import ChevronDownIcon from "../../icons/ChevronDownIcon";
import ChevronRightIcon from "../../icons/ChevronRightIcon";
import styles from "./FilterTree.module.scss";

export interface SubjectExpansionState {
  [id: string]: boolean;
}

const SubjectTopicFilterTree = ({
  filterCounts,
  filterData,
  isSelected,
  setSelected,
  titleId
}: Props) => {
  // We keep expansion state for each subject here.
  // Selection state is external.
  const subjects = sortBy(
    topicsBySubject(filterData.filter(item => !!item.subject)),
    x => !x.computingRelated,
    x => x.name
  );

  const [expandedList, setExpandedList] = useState<SubjectExpansionState>({});
  const setExpanded = (subject: SanitySubject, state: boolean) =>
    setExpandedList({
      ...expandedList,
      [subject._id]: state
    });
  const isExpanded = (subject: SanitySubject): boolean =>
    !!expandedList[subject._id];

  return (
    <>
      <Text variant="filterHeading">
        <GetText id={titleId} />
      </Text>
      <ul className={styles.primaryList}>
        {subjects.map((subject: SubjectWithTopics) => (
          <React.Fragment key={subject._id}>
            <li className={styles.primaryListItem}>
              <>
                <Checkbox
                  id={`checkbox-${subject._id}`}
                  label={
                    <Text variant="filter">
                      <GetText id={subject.name} />
                    </Text>
                  }
                  value={subject._id}
                  onChange={e => {
                    const checked = e.currentTarget.checked;
                    const changes: Record<string, boolean> = {};
                    subject.topics.forEach(t => (changes[t._id] = false));
                    changes[subject._id] = checked;
                    setSelected(changes);
                    if (checked) {
                      setExpanded(subject, true);
                    }
                  }}
                  checked={isSelected(subject)}
                  disabled={!filterCounts[subject._id] && !isSelected(subject)}
                  indeterminate={
                    !isSelected(subject) && subject.topics.some(isSelected)
                  }
                  renderRight={
                    <PlainButton
                      onClick={() => setExpanded(subject, !isExpanded(subject))}
                      className={styles.chevronLink}
                      aria-label={isExpanded(subject) ? "Collapse" : "Expand"}
                      aria-expanded={isExpanded(subject)}
                      id={subject._id}
                    >
                      {expandedList[subject._id] ? (
                        <ChevronDownIcon className={styles.chevronIcon} />
                      ) : (
                        <ChevronRightIcon
                          className={classNames(
                            styles.chevronIcon,
                            styles.chevronIconRight
                          )}
                        />
                      )}
                    </PlainButton>
                  }
                  style={{ height: 35 }}
                />
              </>
            </li>
            {expandedList[subject._id] && (
              <ul
                className={styles.secondaryList}
                aria-labelledby={subject._id}
              >
                {subject.topics.map((topic: FilterType) => (
                  <li key={topic._id} className={styles.secondaryListItem}>
                    <Checkbox
                      id={`checkbox-${topic._id}`}
                      label={
                        <Text variant="filter">
                          <GetText id={topic.name} /> (
                          {filterCounts[topic._id] || 0})
                        </Text>
                      }
                      value={topic._id}
                      onChange={e => {
                        const checked = e.currentTarget.checked;
                        setSelected({
                          [topic._id]: checked,
                          [subject._id]: false
                        });
                      }}
                      checked={isSelected(topic)}
                      disabled={!filterCounts[topic._id] && !isSelected(topic)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </React.Fragment>
        ))}
      </ul>
    </>
  );
};

export default SubjectTopicFilterTree;

interface Props {
  titleId: string;
  filterData: FilterType[];
  filterCounts: FilterCounts;
  isSelected: (filter: FilterType) => boolean;
  setSelected: (selections: Record<string, boolean>) => void;
}

export interface SubjectWithTopics extends SanitySubject {
  topics: SanityTopic[];
}

const initialNumber = (s: string) => {
  const match = /^\d+/.exec(s);
  return match ? parseInt(match[0], 10) : null;
};

// Topics have a subject field but we want a tree rooted at subjects.
const topicsBySubject = (filterData: FilterType[]): SubjectWithTopics[] => {
  const subjectById: { [id: string]: any } = {};
  filterData.forEach(topic => {
    subjectById[topic.subject!._id] = topic.subject;
  });
  const bySubjectId = groupBy(filterData, t => t.subject!._id);
  const result = Object.values(subjectById).map(subject => {
    const topicsForSubject = bySubjectId[subject._id];
    return {
      ...subject,
      topics: sortBy(
        topicsForSubject,
        t => initialNumber(t.name),
        t => t.name
      )
    };
  });
  return result;
};
